import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { fetchEventById } from "../../api/eventsApi";
import { eventStatusLabels, workClassLabels } from "../../domain/operations";
import { executionPercents, isEventReady, isOperationallyComplete, tasksForEvent, unresolvedCriticalRequirements } from "../../domain/operationsQueries";
import { platformServices } from "../../platform/platform";
import { useOperations } from "../../state/OperationsProvider";
import { EventExecutionMeters } from "./EventExecutionMeters";

export function EventWorkspace() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { snapshot, setTaskStatus, addBackendEvent } = useOperations();
  const [loadingRemoteEvent, setLoadingRemoteEvent] = useState(false);
  const [remoteLoadFailed, setRemoteLoadFailed] = useState(false);

  const event = snapshot?.events.find((item) => item.id === eventId);

  useEffect(() => {
    if (!eventId || !snapshot || event) {
      setRemoteLoadFailed(false);
      return;
    }

    let cancelled = false;
    setLoadingRemoteEvent(true);
    setRemoteLoadFailed(false);
    void fetchEventById(eventId)
      .then((loaded) => addBackendEvent(loaded))
      .catch(() => {
        if (!cancelled) setRemoteLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingRemoteEvent(false);
      });

    return () => {
      cancelled = true;
    };
  }, [addBackendEvent, event, eventId, snapshot]);

  if (!snapshot) {
    return (
      <section className="placeholder-view">
        <div><span>Event</span><h2>Loading…</h2></div>
      </section>
    );
  }

  if (!event) {
    if (loadingRemoteEvent) {
      return (
        <section className="placeholder-view">
          <div><span>Event</span><h2>Loading Event…</h2></div>
        </section>
      );
    }
    return (
      <section className="placeholder-view">
        <div>
          <span>Event</span>
          <h2>Event not found</h2>
          <p>{remoteLoadFailed ? "This Event could not be loaded from the server." : "That Event is not in the current operations snapshot."}</p>
          <button onClick={() => navigate("/events")}>Back to Events</button>
        </div>
      </section>
    );
  }

  const tasks = tasksForEvent(snapshot, event.id);
  const percents = executionPercents(tasks);
  const ready = isEventReady(event);
  const operational = isOperationallyComplete(tasks);
  const missing = unresolvedCriticalRequirements(event);
  const lead = snapshot.staff.find((staff) => staff.id === event.leadStaffId);
  const nextWork = tasks.find((task) => task.status !== "complete");
  const currentTime = event.keyTimes[0];
  const sections: { to: string; end: boolean; label: string }[] = [
    { to: `/events/${event.id}`, end: true, label: "Overview" },
    { to: `/events/${event.id}/flow`, end: false, label: "Flow" },
    { to: `/events/${event.id}/commitments`, end: false, label: "Commitments" },
    { to: `/events/${event.id}/planning`, end: false, label: "Planning" },
    { to: `/events/${event.id}/requirements`, end: false, label: "Requirements" },
    { to: `/events/${event.id}/work`, end: false, label: "Work" },
    { to: `/events/${event.id}/conditions`, end: false, label: "Conditions" },
  ];

  return (
    <div className="event-page">
      <section className="desktop-view event-desktop">
        <button className="text-button event-back" type="button" onClick={() => navigate("/events")}>← Events</button>
        <header className="event-header">
          <div>
            <div className="date-line"><span>{event.type}</span><span>•</span><span>{event.client}</span></div>
            <h1>{event.name}</h1>
            <p className="event-meta">{event.dateRangeLabel ?? event.dateLabel} · {event.location} · {event.headcount} guests · {event.selectedService}</p>
          </div>
          <div className="event-header-flags">
            <span className={`status-pill ${event.status}`}>{eventStatusLabels[event.status]}</span>
            <span className={`ready-pill ${ready ? "ready" : "not-ready"}`}>{ready ? "Event Ready" : "Not Event Ready"}</span>
            <span className={`ready-pill ${operational ? "ready" : "not-ready"}`}>{operational ? "Operationally Complete" : "Operations open"}</span>
          </div>
        </header>
        <EventExecutionMeters percents={percents} />
        {missing.length > 0 && <div className="event-alert">Progress is not readiness. {missing.map((item) => item.title).join("; ")} still unresolved.</div>}
        {event.status === "cancelled" && <div className="event-alert caution">Cancelled. History is preserved. This Event does not participate in active Operations.</div>}
        {event.status === "completed" && !operational && <div className="event-alert caution">Event is Completed. Outstanding work — especially Reset — remains in Operations until finished.</div>}
        <div className="event-lead-line">Lead {lead?.name ?? "Unassigned"} · {event.contacts.filter((contact) => contact.external).map((contact) => contact.name).join(" · ")}</div>
        <nav className="event-tabs" aria-label="Event workspace">
          {sections.map((section) => (
            <NavLink key={section.label} to={section.to} end={section.end} className={({ isActive }) => isActive ? "active" : ""}>{section.label}</NavLink>
          ))}
        </nav>
      </section>

      <section className="mobile-view event-mobile">
        <button className="text-button event-back" type="button" onClick={() => navigate("/today")}>← Today</button>
        <div className="mobile-date">
          <div>
            <span>{eventStatusLabels[event.status].toUpperCase()}</span>
            <h1>{event.name}</h1>
          </div>
          {currentTime && <strong className="mobile-key-time">{currentTime.timeLabel}</strong>}
        </div>
        {event.location && (
          <button className="work-block-link" type="button" onClick={() => platformServices.openDirections(event.location!)}>
            <span className="location-icon">⌖</span>
            <span><strong>{event.location}</strong><small>Directions · {event.dateLabel}</small></span>
            <span>›</span>
          </button>
        )}
        {nextWork && (
          <div className="mobile-next-work">
            <span>Next work</span>
            <strong>{nextWork.title}</strong>
            <small>{workClassLabels[nextWork.classification]}</small>
          </div>
        )}
        <nav className="event-tabs" aria-label="Event workspace">
          {sections.map((section) => (
            <NavLink key={section.label} to={section.to} end={section.end} className={({ isActive }) => isActive ? "active" : ""}>{section.label}</NavLink>
          ))}
        </nav>
      </section>

      <Outlet context={{ event, snapshot, setTaskStatus }} />
    </div>
  );
}
